/* DraftCasesCard — surfaces in-progress case drafts so doctors can
 * pick up where they left off.
 *
 * iter90: addresses a real bug — `Cases.jsx` filters `status === "draft"`
 * out of the main list, so once a doctor abandons the wizard partway
 * the case becomes invisible. This card lives at the top of the
 * Aligner Cases page and lists drafts with:
 *   - patient + diagnosis (so the doctor recognises the case)
 *   - last-touched timestamp
 *   - inferred furthest-reached step (the heuristic below)
 *   - "Continue" → deep-links to /doctor/cases/new?draft=...&step=N
 *   - "Discard" → soft-cancels the draft via the existing endpoint
 *
 * Self-hides when there are no drafts so it doesn't clutter the page
 * for doctors with a clean inbox.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trash, ClockClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";

const STEP_LABELS = {
    1: "Case Setup",
    2: "Clinical Exam",
    3: "Scans & Photos",
    4: "Treatment Plan",
    5: "Attachments",
    6: "Lab Instructions",
    7: "Review & Submit",
};

/**
 * computeResumeStep — heuristic that figures out the furthest step the
 * doctor reached before abandoning. Used to deep-link the Continue
 * button. The heuristic looks for the first step whose data is empty;
 * returns 1 if nothing is filled (just patient picked).
 *
 * Strict-by-step approach: once a step is "incomplete" we return it.
 * We deliberately don't try to skip ahead past an empty step — better
 * to land the doctor on the field they need to fill than to surface a
 * jarring "we put you 3 steps ahead with no context" experience.
 */
export function computeResumeStep(c) {
    if (!c) return 1;
    // Step 1: needs patient + planning_mode + tier
    if (!c.patient_id || !c.planning_mode || !c.pricing?.tier_id) return 1;
    // Step 2: needs diagnosis
    if (!c.diagnosis) return 2;
    // Step 3: needs at least one uploaded file OR scan provenance set
    const hasFiles = (c.files || []).some((f) => !f.is_deleted);
    const hasScanProvenance = c.clinical?.scan_device || c.clinical?.scan_date;
    if (!hasFiles && !hasScanProvenance) return 3;
    // Step 4: needs objectives (both modes) — and movements for self-plan.
    // iter97.60 merged attachments/cuts/ramps into Step 4 (Treatment
    // Plan); they're optional finishing touches, so we don't gate on
    // them here. Objectives + movements are the "did they author a
    // plan?" signal for self-plan; CoE mode only needs objectives.
    const hasObjectives = (c.prescription?.objectives || []).length > 0;
    if (!hasObjectives) return 4;
    if (c.planning_mode === "doctor"
        && (c.prescription?.tooth_movements_plan || []).length === 0) return 4;
    // Step 5 · Lab Instructions
    if (!c.prescription?.lab_notes) return 5;
    // Otherwise land on Review (Step 6)
    return 6;
}

/**
 * computeDraftProgress — iter101.83.
 *
 * Returns `{ done, total }` where `done` is the number of wizard steps
 * that have meaningful data on this draft. Used by the "Treatment
 * progress" column on My Aligner Cases so the row honestly reflects
 * work-in-flight, not just Step-1 gate satisfaction.
 *
 * iter101.84 — Now ALSO returns a `steps` array so callers (row-level
 * progress-bar tooltip) can show per-step ticks vs pending circles.
 * Shape: `[{ id, label, done }, …]` in wizard order. Length is always
 * 6 regardless of planning mode (both Doctor-Planned and Invisique-
 * Planned flows share the same 6-step wizard).
 *
 * iter101.86 — Step labels were WRONG (had a "Attachments & Cuts" ghost
 * step that doesn't actually exist in the wizard). Actual layout per
 * `STEPS_COE` / `STEPS_SELF` in CaseSubmissionPage.jsx:
 *   1. Case Setup       — patient + planning mode + tier / mfg speed
 *   2. Clinical Exam    — diagnosis
 *   3. Scans & Photos   — files or scan provenance
 *   4. Treatment Plan   — objectives (+ movements/attachments/cuts/ramps
 *                          merged into Step 4 in iter97.60 for self-plan)
 *   5. Lab Instructions — free-text lab_notes to the manufacturer
 *   6. Review & Submit  — final consent + submit
 *
 * Why not just reuse `computeResumeStep`?
 * ---------------------------------------
 * `computeResumeStep` is intentionally strict-by-step — it returns the
 * FIRST step that isn't fully done so the resume deep-link lands the
 * doctor on the exact field they need to fill. That's the right
 * behaviour for navigation but the WRONG source of truth for a
 * progress counter: doctors regularly fill downstream steps out of
 * order (e.g., attach scans before picking a manufacturing tier),
 * which leaves Step 1 "incomplete" per the strict check, so
 * `stepsDone = resumeStep − 1` reads `0/6` even after they've filled
 * five actual fields. Users report this as a bug.
 *
 * Heuristic: mark each of the 6 wizard steps individually. Step 1 gets
 * credit whenever ANY downstream step has data, because the wizard's
 * own gating implies the doctor must have passed Step 1's checkpoint
 * to reach later steps. This makes the counter forgiving without ever
 * over-counting.
 */
export function computeDraftProgress(c) {
    if (!c) return { done: 0, total: 6, steps: [] };

    // Step 2 · Clinical Exam
    const step2Done = !!c.diagnosis;
    // Step 3 · Scans & Photos
    const step3Done = ((c.files || []).some((f) => !f.is_deleted))
        || !!(c.clinical?.scan_device || c.clinical?.scan_date);
    // Step 4 · Treatment Plan
    // For self-plan the doctor authors objectives + tooth movements
    // (attachments/cuts/ramps live on the same step but are optional
    // finishing touches — objectives + movements are the "did they
    // author a plan?" signal). For CoE mode the planner drafts it, so
    // objectives alone is the signal the doctor has provided direction.
    const hasObjectives = (c.prescription?.objectives || []).length > 0;
    const hasMovements = (c.prescription?.tooth_movements_plan || []).length > 0;
    const step4Done = c.planning_mode === "doctor"
        ? (hasObjectives && hasMovements)
        : hasObjectives;
    // Step 5 · Lab Instructions
    const step5Done = !!c.prescription?.lab_notes;
    // Step 6 · Review & Submit
    // Signed consent implies the doctor has reached and completed the
    // Review step. Anything short of a signed consent means they haven't
    // truly landed on Step 6's outcome.
    const step6Done = !!c.prescription_compliance?.signed_at;

    // Step 1 · Case Setup — tight check OR inferred from downstream data.
    const step1Tight = !!(c.patient_id && c.planning_mode && c.pricing?.tier_id);
    const anyDownstream = step2Done || step3Done || step4Done || step5Done || step6Done;
    const step1Done = step1Tight || anyDownstream;

    const steps = [
        { id: 1, label: "Case Setup",       done: step1Done },
        { id: 2, label: "Clinical Exam",    done: step2Done },
        { id: 3, label: "Scans & Photos",   done: step3Done },
        { id: 4, label: "Treatment Plan",   done: step4Done },
        { id: 5, label: "Lab Instructions", done: step5Done },
        { id: 6, label: "Review & Submit",  done: step6Done },
    ];
    return { done: steps.filter((s) => s.done).length, total: 6, steps };
}

function formatRelative(iso) {
    if (!iso) return "—";
    const then = new Date(iso);
    const now = Date.now();
    const diffMin = Math.round((now - then.getTime()) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin} min ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hr ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
    return then.toLocaleDateString();
}


function ProgressBar({ step }) {
    const pct = Math.round((step / 7) * 100);
    return (
        <div className="space-y-1">
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="text-[10px] text-muted-foreground">
                Step {step} of 7 · {STEP_LABELS[step]}
            </div>
        </div>
    );
}


function DraftRow({ draft, onDiscard, busy }) {
    const resumeStep = computeResumeStep(draft);
    const href = `/doctor/cases/new?draft=${draft.id}&step=${resumeStep}`;
    return (
        <li
            className="rounded-lg border border-border bg-card hover:border-primary/30 hover:bg-primary/[0.02] transition-colors"
            data-testid={`draft-row-${draft.id}`}
        >
            <div className="flex items-start gap-3 p-3 sm:p-4">
                <div className="w-9 h-9 rounded-full bg-amber-100 grid place-items-center shrink-0">
                    <ClockClockwise size={16} weight="bold" className="text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate">
                            {draft.patient_name || "Unnamed patient"}
                        </span>
                        {draft.patient_phone && (
                            <span className="text-[11px] text-muted-foreground">
                                {draft.patient_phone}
                            </span>
                        )}
                        <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider bg-amber-50 px-1.5 py-0.5 rounded">
                            Draft
                        </span>
                    </div>
                    <div className="text-[12px] text-muted-foreground mt-0.5 truncate">
                        {draft.diagnosis || <span className="italic">No diagnosis yet</span>}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                        Last updated {formatRelative(draft.updated_at || draft.created_at)}
                    </div>
                    <div className="mt-2.5 max-w-md">
                        <ProgressBar step={resumeStep} />
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 shrink-0">
                    <Link
                        to={href}
                        className="inline-flex items-center justify-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
                        data-testid={`draft-continue-${draft.id}`}
                    >
                        Continue
                        <ArrowRight size={12} weight="bold" />
                    </Link>
                    <button
                        type="button"
                        onClick={() => onDiscard(draft)}
                        disabled={busy}
                        className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50 px-2 py-1.5 rounded-md transition-colors disabled:opacity-50"
                        data-testid={`draft-discard-${draft.id}`}
                    >
                        <Trash size={11} />
                        Discard
                    </button>
                </div>
            </div>
        </li>
    );
}


export default function DraftCasesCard({ cases, onChanged }) {
    const drafts = (cases || []).filter((c) => c.status === "draft");
    const [busy, setBusy] = useState(false);

    if (drafts.length === 0) return null;

    const handleDiscard = async (draft) => {
        const label = draft.patient_name || "this draft";
        if (!(await confirmDialog({
            title: `Discard the draft case for ${label}?`,
            message: "This can't be undone.",
            confirmText: "Discard",
            tone: "danger",
        }))) return;
        setBusy(true);
        try {
            // Use the existing cancel endpoint — drafts go to status
            // "cancelled" instead of being hard-deleted, which preserves
            // an audit trail of intent without polluting the active list.
            await api.put(`/cases/${draft.id}/cancel`, {
                reason: "Discarded by doctor before submission",
            });
            toast.success("Draft discarded");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't discard the draft");
        } finally {
            setBusy(false);
        }
    };

    // Sort by most-recently-touched first so the active draft surfaces top.
    const sorted = [...drafts].sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
    });

    return (
        <section
            className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 sm:p-5"
            data-testid="drafts-card"
        >
            <div className="flex items-baseline justify-between gap-2 mb-3">
                <div>
                    <h3 className="font-display text-base font-bold text-amber-900">
                        Drafts in progress · {drafts.length}
                    </h3>
                    <p className="text-[11.5px] text-amber-900/70 mt-0.5">
                        Cases you've started but haven't submitted yet. Pick up where you left off.
                    </p>
                </div>
            </div>
            <ul className="space-y-2">
                {sorted.map((d) => (
                    <DraftRow key={d.id} draft={d} onDiscard={handleDiscard} busy={busy} />
                ))}
            </ul>
        </section>
    );
}
